// /src/pages/private/members/MembersOnboardingEnding.jsx
// Closing screen: ending-page background + dashboard-style hero card overlay.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../authContext';
import { useSecurity, VIEW_AS_HEADER } from '../../../SecurityContext';
import { fetchMemberProfileCard } from '../../../components/tools/memberProfileCardApi';
import discordAvatarDefault from '../../../medias/avatar_default.png';
import orgLogo from '../../../medias/logo.png';
import MembersOnboardingHeroBackground, { clamp01 } from './onboarding/MembersOnboardingHeroBackground';
import { onboardingBackgroundKind } from '../../../utils/onboardingLandingUi';
import OnboardingHero from './onboarding/OnboardingHero';
import MembersOnboardingEndingStructureStrip from './onboarding/MembersOnboardingEndingStructureStrip';
import MembersOnboardingEndingUpcomingEvents from './onboarding/MembersOnboardingEndingUpcomingEvents';
import { fetchRosterFile } from './rosterTools/api/rosterFileApi';
import { mergeRosterAndTraining } from './rosterTools/data/rosterData';
import { displayMoniker, pickPrimaryHandleIdFromHandleList } from './personalhub/dashboard/dashboardUtils';
import { structureCardsFromRosterPlayer } from './personalhub/dashboard/structureCardsUtils';
import { completeMemberOnboardingOnServer } from '../../../utils/onboardingStorage';
import { logOnboardingGateDecision } from '../../../utils/onboardingGateLog';

const ENDING_IMG_CLASS =
  'members-onboarding-ending__media-el members-onboarding-ending__media-el--image';
const ENDING_VIDEO_CLASS =
  'members-onboarding-ending__media-el members-onboarding-ending__media-el--video';

function normalizeEndingOfficers(raw) {
  if (!Array.isArray(raw)) return [];
  const seenUserIds = new Set();
  const seenNames = new Set();
  const out = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const userId = item.userId != null ? Number(item.userId) : null;
    const discordName = item.discordName != null ? String(item.discordName).trim() : '';
    if (!discordName) continue;
    if (userId != null && Number.isFinite(userId)) {
      if (seenUserIds.has(userId)) continue;
      seenUserIds.add(userId);
    } else {
      const key = discordName.toLowerCase();
      if (seenNames.has(key)) continue;
      seenNames.add(key);
    }
    const rankImageUrl = item.rankImageUrl != null ? String(item.rankImageUrl).trim() : '';
    out.push({
      userId,
      discordName,
      rankImageUrl: rankImageUrl || null,
    });
  }
  return out;
}

function EndingOfficerChips({ officers }) {
  if (!Array.isArray(officers) || officers.length === 0) return null;
  return (
    <div className="members-onboarding-ending__officerRow">
      {officers.map((officer) => (
        <span
          key={officer.userId ?? officer.discordName}
          className="members-onboarding-ending__officerChip"
        >
          {officer.rankImageUrl ? (
            <img
              className="members-onboarding-ending__officerRankIcon"
              src={officer.rankImageUrl}
              alt=""
              aria-hidden
            />
          ) : (
            <span
              className="members-onboarding-ending__officerRankIcon members-onboarding-ending__officerRankIcon--empty"
              aria-hidden
            />
          )}
          <span className="members-onboarding-ending__officerName">{officer.discordName}</span>
        </span>
      ))}
    </div>
  );
}

export default function MembersOnboardingEnding() {
  const navigate = useNavigate();
  const { handles, isLoggedIn, timezone, email, userId, discordName, gcuAccepted } = useAuth();
  const { isViewAsActive, viewAsUserId } = useSecurity();

  const [payload, setPayload] = useState(null);
  const [endingLoading, setEndingLoading] = useState(true);
  const [endingError, setEndingError] = useState(null);

  const [player, setPlayer] = useState(null);
  const [playerLoading, setPlayerLoading] = useState(true);
  const [playerError, setPlayerError] = useState(null);

  const mediaLayerRef = useRef(null);

  const onDiscordAvatarError = useCallback((e) => {
    const img = e?.currentTarget;
    if (!img) return;
    img.onerror = null;
    img.src = discordAvatarDefault;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setEndingLoading(true);
        setEndingError(null);
        const requestConfig = { withCredentials: true };
        if (isViewAsActive && viewAsUserId != null) {
          requestConfig.headers = { [VIEW_AS_HEADER]: String(viewAsUserId) };
        }
        const { data } = await axios.get('/api/user/onboarding/ending-page', requestConfig);
        if (!cancelled) setPayload(data || {});
      } catch (e) {
        if (!cancelled) {
          setEndingError('Unable to load onboarding ending.');
          setPayload({});
        }
      } finally {
        if (!cancelled) setEndingLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isViewAsActive, viewAsUserId]);

  useEffect(() => {
    let cancelled = false;

    async function loadDashboardProfile() {
      if (!isLoggedIn) {
        setPlayer(null);
        setPlayerLoading(false);
        setPlayerError(null);
        return;
      }

      setPlayerLoading(true);
      setPlayerError(null);

      let handleId = null;

      if (isViewAsActive && viewAsUserId != null) {
        try {
          const cardRes = await fetchMemberProfileCard(viewAsUserId);

          if (cancelled) return;

          if (!cardRes?.ok) {
            setPlayer(null);
            setPlayerError(cardRes?.error || 'Could not load member profile.');
            setPlayerLoading(false);
            return;
          }

          handleId = pickPrimaryHandleIdFromHandleList(cardRes.card?.handles);
        } catch (e) {
          if (cancelled) return;

          setPlayer(null);
          setPlayerError(e?.message || 'Could not load member profile.');
          setPlayerLoading(false);
          return;
        }
      } else {
        handleId = pickPrimaryHandleIdFromHandleList(handles);
      }

      if (!handleId) {
        if (!cancelled) {
          setPlayer(null);
          setPlayerError(null);
          setPlayerLoading(false);
        }
        return;
      }

      try {
        const data = await fetchRosterFile(handleId);

        if (cancelled) return;

        if (!data?.ok) {
          setPlayer(null);
          setPlayerError(data?.error || 'Could not load profile.');
          setPlayerLoading(false);
          return;
        }

        const trainingsByHandleId = {
          [String(handleId)]: data.player?.trainings || [],
        };

        const merged = mergeRosterAndTraining([data.player], trainingsByHandleId);
        setPlayer(merged[0] || null);
      } catch (e) {
        if (cancelled) return;

        setPlayer(null);
        setPlayerError(e?.message || 'Could not load profile.');
      } finally {
        if (!cancelled) setPlayerLoading(false);
      }
    }

    loadDashboardProfile();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, isViewAsActive, viewAsUserId, handles]);

  const bgMedia = payload?.backgroundMedia || null;

  useLayoutEffect(() => {
    const el = mediaLayerRef.current;
    if (!el) return;
    const fx = clamp01(Number(bgMedia?.focalX));
    const fy = clamp01(Number(bgMedia?.focalY));
    el.style.setProperty('--mob-focal-x', `${fx * 100}%`);
    el.style.setProperty('--mob-focal-y', `${fy * 100}%`);
  }, [bgMedia]);

  const hasBackground = useMemo(() => onboardingBackgroundKind(bgMedia) !== 'none', [bgMedia]);

  const stageClassName = [
    'members-onboarding-ending__stage',
    hasBackground ? 'members-onboarding-ending__stage--has-media' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const audience = payload?.audience === 'newMember' ? 'newMember' : 'existingMember';

  const rosterMoniker = displayMoniker(player);
  const copyMoniker = useMemo(() => {
    if (player) return rosterMoniker;
    const fromApi = payload?.copy?.moniker != null ? String(payload.copy.moniker).trim() : '';
    return fromApi || '—';
  }, [player, payload?.copy?.moniker, rosterMoniker]);

  const copyWelcoming = payload?.copy?.welcoming != null ? String(payload.copy.welcoming).trim() : '';
  const copySubtitle = payload?.copy?.subtitle != null ? String(payload.copy.subtitle).trim() : '';
  const copyDescription = payload?.copy?.description != null ? String(payload.copy.description).trim() : '';
  const copyContactingAnOfficer =
    payload?.copy?.contactingAnOfficer != null ? String(payload.copy.contactingAnOfficer).trim() : '';

  const officerRowsExecutive = useMemo(
    () => normalizeEndingOfficers(payload?.officerRows?.executive),
    [payload?.officerRows?.executive],
  );
  const officerRowsSenior = useMemo(
    () => normalizeEndingOfficers(payload?.officerRows?.senior),
    [payload?.officerRows?.senior],
  );

  const hasOfficers = officerRowsExecutive.length > 0 || officerRowsSenior.length > 0;
  const showWebsiteTextCard = Boolean(copyContactingAnOfficer) || hasOfficers;

  const structureCardsForStrip = useMemo(() => {
    const fromApi = payload?.structureCards;
    if (Array.isArray(fromApi) && fromApi.length > 0) return fromApi;
    return structureCardsFromRosterPlayer(player);
  }, [payload?.structureCards, player]);

  const handleAccessWebsite = useCallback(async () => {
    try {
      const result = await completeMemberOnboardingOnServer({
        useViewAsSubject: isViewAsActive,
      });
      logOnboardingGateDecision({
        label: 'MembersOnboardingEnding → navigate /members/personalhub (after complete)',
        identity: { email, discordName, handles, gcuAccepted },
        onboardingCompletedAt: result?.onboardingCompletedAt ?? null,
        subjectOnboardingCompletedAt: isViewAsActive ? result?.onboardingCompletedAt ?? null : null,
        isViewAsActive,
        isSuperAdmin: false,
        viewAsUserId,
        userId,
        pathname: '/members/onboarding/ending',
      });
      navigate('/members/personalhub', { replace: true });
    } catch (e) {
      console.error('[onboarding-gate] POST /api/user/onboarding/complete failed', e?.response?.data ?? e);
    }
  }, [navigate, isViewAsActive, email, discordName, handles, gcuAccepted, viewAsUserId, userId]);

  return (
    <div
      className="private-pages members-onboarding-ending"
      data-resource="members.onboarding.ending"
      data-action="read"
    >
      <div className={stageClassName}>
        <div ref={mediaLayerRef} className="members-onboarding-ending__media-layer">
          <MembersOnboardingHeroBackground
            media={bgMedia}
            imageClassName={ENDING_IMG_CLASS}
            videoClassName={ENDING_VIDEO_CLASS}
          />
        </div>
        <div className="members-onboarding-ending__scrim" aria-hidden />

        <div
          className="members-onboarding-ending__dashboardDock"
          data-resource="members.onboarding.ending"
          data-action="read"
          data-description="Onboarding ending — hero and structure recap"
        >
          <div className="members-onboarding-ending__heroRow">
            <div className="members-onboarding-ending__heroStack">
              <OnboardingHero player={player} onDiscordAvatarError={onDiscordAvatarError} />
              <MembersOnboardingEndingStructureStrip cards={structureCardsForStrip} />
            </div>
            <MembersOnboardingEndingUpcomingEvents
              events={payload?.upcomingEvents}
              loading={endingLoading}
              timeZone={timezone}
            />
            <div className="members-onboarding-ending__websiteCard">
              {showWebsiteTextCard ? (
                <div
                  className="members-onboarding-ending__websiteTextCard"
                  data-resource="members.onboarding.ending"
                  data-action="read"
                  data-description="Onboarding ending — contacting an officer"
                >
                  {copyContactingAnOfficer ? (
                    <p className="members-onboarding-ending__websiteText">{copyContactingAnOfficer}</p>
                  ) : null}
                  {hasOfficers ? (
                    <div className="members-onboarding-ending__officerRows">
                      <EndingOfficerChips officers={officerRowsExecutive} />
                      <EndingOfficerChips officers={officerRowsSenior} />
                    </div>
                  ) : null}
                </div>
              ) : null}
              <button
                type="button"
                className="members-onboarding-ending__websiteButton"
                onClick={handleAccessWebsite}
                data-resource="members.onboarding.ending"
                data-action="execute"
                data-route="/members/personalhub"
                data-description="Onboarding ending — access our website"
              >
                access our website
              </button>
            </div>
          </div>
        </div>

        <div className="members-onboarding-ending__inner">
          {endingLoading ? <p className="members-onboarding-ending__status">Loading…</p> : null}
          {endingError ? (
            <p className="members-onboarding-ending__status members-onboarding-ending__status--error">{endingError}</p>
          ) : null}
          {!endingLoading && !endingError ? (
            <>
              <img
                className="members-onboarding-ending__orgLogo"
                src={orgLogo}
                alt=""
                aria-hidden
                data-resource="members.onboarding.ending"
                data-action="read"
                data-description="Onboarding ending — organization logo"
              />
              <div
                className="members-onboarding-ending__copy"
                data-resource="members.onboarding.ending"
                data-action="read"
                data-description="Onboarding ending → Left copy"
              >
              {audience === 'existingMember' && copyWelcoming ? (
                <p className="members-onboarding-ending__welcoming">{copyWelcoming}</p>
              ) : null}
              <p className="members-onboarding-ending__headline">{copyMoniker}</p>
              {copySubtitle ? <p className="members-onboarding-ending__subline">{copySubtitle}</p> : null}
              {copyDescription ? (
                <p className="members-onboarding-ending__lead">{copyDescription}</p>
              ) : null}
            </div>
            </>
          ) : null}
          {playerError ? (
            <p className="members-onboarding-ending__status members-onboarding-ending__status--error">{playerError}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
